import { AriaLivePoliteness, LiveAnnouncer } from '@angular/cdk/a11y';
import { inject, Injectable } from '@angular/core';

/**
 * Thin wrapper over the CDK {@link LiveAnnouncer} for WCAG 4.1.3 Status Messages.
 *
 * In a single-page app, screen readers are not told about changes that happen
 * without a page reload (route changes, toast notifications, async results,
 * save/error feedback). Inject this service and call {@link announce} to push a
 * message into the polite/assertive live region so it is read out immediately.
 */
@Injectable({ providedIn: 'root' })
export class A11yAnnouncerService {
  private readonly liveAnnouncer = inject(LiveAnnouncer);

  /**
   * Announce a status message to assistive technology.
   *
   * @param message Human-readable, already-translated text.
   * @param politeness `polite` (default) waits for the screen reader to finish
   *   what it is reading; `assertive` interrupts — reserve it for errors.
   */
  announce(message: string, politeness: AriaLivePoliteness = 'polite'): void {
    if (!message) {
      return;
    }
    this.liveAnnouncer.announce(message, politeness);
  }
}
